// src/components/layout/Header.js
import React, { useState } from 'react';
import { Code, Moon, Sun } from 'lucide-react';
import { Link } from 'react-scroll';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { translations } from '../../data/translations';
import { useOnScroll } from '../../hooks/useOnScroll';
import { useActiveSection } from '../../hooks/useActiveSection';
import LanguageToggle from '../ui/LanguageToggle';

const Header = () => {
  const { darkMode, toggleDarkMode } = useTheme();
  const { language } = useLanguage();
  const t = translations[language];
  const scrolled = useOnScroll(50);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const activeSection = useActiveSection(['accueil', 'about', 'competences', 'projets', 'parcours', 'contact', 'blog']);
  
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const navItems = [
    { id: 'accueil', label: t["nav.home"] },
    { id: 'about', label: t["nav.about"] },
    { id: 'competences', label: t["nav.skills"] },
    { id: 'projets', label: t["nav.projects"] },
    { id: 'parcours', label: t["nav.experience"] },
    { id: 'contact', label: t["nav.contact"] },
    { id: 'blog', label: t["nav.blog"] }
  ];

  return (
    <header className={`fixed w-full transition-all duration-500 z-50 ${
      scrolled ? 'bg-white dark:bg-gray-800 bg-opacity-90 dark:bg-opacity-90 shadow-md' : 'bg-opacity-0'
    }`}>
      <div className="container mx-auto flex justify-between items-center py-4 px-6">
        <div className="flex items-center">
          <Code className={`h-8 w-8 ${darkMode ? 'text-blue-400' : 'text-blue-600'} mr-2`} />
          <span className="font-bold text-xl">Igor Sibemou</span>
        </div>
        
        {/* Navigation Desktop */}
        <nav className="hidden md:flex space-x-6">
          {navItems.map(item => (
            <Link
              key={item.id}
              to={item.id}
              spy={true}
              smooth={true}
              offset={-70}
              duration={500}
              className={`transition-colors duration-200 hover:text-blue-500 cursor-pointer ${
                activeSection === item.id ? (darkMode ? 'text-blue-400' : 'text-blue-600') : ''
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>
        
        <div className="flex items-center space-x-4">
          <LanguageToggle />
          
          <button 
            onClick={toggleDarkMode} 
            className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
            aria-label={darkMode ? "Activer le mode clair" : "Activer le mode sombre"}
          >
            {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
          
          {/* Mobile Menu Button */}
          <button 
            onClick={toggleMenu} 
            className="md:hidden p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
            aria-label="Menu"
            aria-expanded={isMenuOpen}
          >
            <div className="w-6 flex flex-col items-center">
              <span className={`block w-full h-0.5 mb-1 transition-transform duration-300 ${
                darkMode ? 'bg-white' : 'bg-black'
              } ${isMenuOpen ? 'transform rotate-45 translate-y-1.5' : ''}`}></span>
              <span className={`block w-full h-0.5 mb-1 transition-opacity duration-300 ${
                darkMode ? 'bg-white' : 'bg-black'
              } ${isMenuOpen ? 'opacity-0' : ''}`}></span>
              <span className={`block w-full h-0.5 transition-transform duration-300 ${
                darkMode ? 'bg-white' : 'bg-black'
              } ${isMenuOpen ? 'transform -rotate-45 -translate-y-1.5' : ''}`}></span>
            </div>
          </button>
        </div>
      </div>
      
      {/* Mobile Menu */}
      <div className={`md:hidden transition-all duration-300 overflow-hidden ${
        isMenuOpen ? 'max-h-80' : 'max-h-0'
      } ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="px-6 py-4 space-y-4">
          {navItems.map(item => (
            <Link
              key={item.id}
              to={item.id}
              spy={true}
              smooth={true}
              offset={-70}
              duration={500}
              onClick={closeMenu}
              className="block w-full text-left py-2 hover:text-blue-500"
            >
              {item.label}
            </Link>
          ))}
        </div>
      </div>
    </header>
  );
};

export default Header;